// src/views/portal/PortalNotificacoesForm.jsx
// Aba "Notificações" do modal Perfil: gerencia a inscrição em Web Push.
import React, { useEffect, useState, useCallback } from "react";
import Banner from "../../components/ui/Banner";
import {
  pushSuportado,
  statusAssinaturaAtual,
  servidorHabilitado,
  ativarPush,
  desativarPush,
  testarPush,
} from "../../utils/pushNotifications";

const TEXTOS = {
  condomino: "Receba alertas sobre boletos, comunicados, encomendas e respostas direto no seu navegador — mesmo com o site fechado.",
  portaria: "Receba avisos urgentes dos condôminos, encomendas e alertas da portaria neste dispositivo — mesmo com o navegador fechado.",
};

export default function PortalNotificacoesForm({ variant = "condomino" }) {
  const pushOpts = variant === "portaria" ? { portaria: true } : { admin: false };
  const [status, setStatus] = useState({ suportado: false, assinado: false, permissao: "default" });
  const [server, setServer] = useState({ checked: false, enabled: false });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const recarregar = useCallback(async () => {
    if (!pushSuportado()) {
      setStatus({ suportado: false, assinado: false });
      setServer({ checked: true, enabled: false });
      return;
    }
    const [s, srv] = await Promise.all([
      statusAssinaturaAtual(pushOpts),
      servidorHabilitado(pushOpts),
    ]);
    setStatus(s);
    setServer({ checked: true, enabled: srv.enabled });
  }, [variant]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const ligar = async () => {
    setLoading(true); setMsg(null);
    try {
      await ativarPush(pushOpts);
      setMsg({ tipo:"success", texto:"Notificações ativadas com sucesso!" });
      await recarregar();
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Não foi possível ativar." });
    } finally { setLoading(false); }
  };

  const desligar = async () => {
    setLoading(true); setMsg(null);
    try {
      await desativarPush(pushOpts);
      setMsg({ tipo:"info", texto:"Notificações desativadas neste dispositivo." });
      await recarregar();
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Erro ao desativar." });
    } finally { setLoading(false); }
  };

  const testar = async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await testarPush(pushOpts);
      setMsg({
        tipo: r?.sent > 0 ? "success" : "warning",
        texto: r?.message || "Teste enviado.",
      });
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Erro no teste." });
    } finally { setLoading(false); }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{fontSize:13,color:"var(--gray-500)"}}>
        {TEXTOS[variant] || TEXTOS.condomino}
      </div>

      {msg && <Banner tipo={msg.tipo}>{msg.texto}</Banner>}

      {!status.suportado ? (
        <div style={{
          padding:14,background:"var(--bg-surface-alt)",border:"1px solid var(--border-color)",borderRadius:10,
          textAlign:"center",color:"var(--gray-500)",fontSize:13,
        }}>
          <i className="bi bi-info-circle" style={{fontSize:24,color:"var(--gray-400)"}}/>
          <div style={{marginTop:6}}>
            Este navegador não suporta notificações push.<br/>
            Use Chrome, Edge ou Firefox no desktop, ou instale o app no celular.
          </div>
        </div>
      ) : server.checked && !server.enabled ? (
        <div style={{
          padding:14,background:"var(--bg-warning-soft)",border:"1px solid #FDE68A",borderRadius:10,
          color:"#92400E",fontSize:13,
        }}>
          <i className="bi bi-shield-exclamation me-2"/>
          <strong>Notificações temporariamente indisponíveis.</strong><br/>
          O servidor ainda não está configurado para enviar notificações push.
          Tente novamente em alguns minutos ou avise a administração do condomínio.
        </div>
      ) : status.permissao === "denied" ? (
        <div style={{
          padding:14,background:"var(--bg-danger-soft)",border:"1px solid #FCA5A5",borderRadius:10,
          color:"#991B1B",fontSize:13,
        }}>
          <i className="bi bi-bell-slash me-2"/>
          Você bloqueou as notificações deste site no navegador. Para reativar, acesse as configurações do site no navegador e libere "Notificações".
        </div>
      ) : status.assinado ? (
        <>
          <div style={{
            padding:14,background:"var(--bg-success-soft)",border:"1px solid #86EFAC",borderRadius:10,
            color:"#166534",fontSize:13,
            display:"flex",alignItems:"center",gap:10,
          }}>
            <i className="bi bi-bell-fill" style={{fontSize:22}}/>
            <div>
              <div style={{fontWeight:700}}>Notificações ativas neste dispositivo</div>
              <div style={{fontSize:11,marginTop:2,opacity:.8}}>
                Você receberá alertas mesmo quando o navegador estiver fechado.
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={loading} onClick={testar}>
              <i className="bi bi-send me-1"/>
              Enviar teste
            </button>
            <button type="button" className="btn btn-outline-danger btn-sm" disabled={loading} onClick={desligar}>
              <i className="bi bi-bell-slash me-1"/>
              Desativar neste dispositivo
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{
            padding:14,background:"var(--bg-highlight)",border:"1px solid #BFDBFE",borderRadius:10,
            color:"#1D4ED8",fontSize:13,
          }}>
            <i className="bi bi-bell me-2"/>
            <strong>Notificações desativadas.</strong> Ative para receber alertas em tempo real.
          </div>
          <button type="button" className="btn btn-primary" disabled={loading} onClick={ligar}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-2"/>Ativando...</>
              : <><i className="bi bi-bell-fill me-2"/>Ativar notificações</>}
          </button>
        </>
      )}

      <div style={{fontSize:11,color:"var(--gray-400)",lineHeight:1.4,marginTop:6}}>
        <strong>Privacidade:</strong> as notificações são enviadas diretamente ao seu navegador.
        Você pode desativá-las a qualquer momento, neste mesmo lugar.
      </div>
    </div>
  );
}
